import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const getAuthToken = () => {
  const adminData = sessionStorage.getItem('adminkhadamat');
  if (adminData) {
    try {
      const parsed = JSON.parse(adminData);
      // البحث عن التوكن في أماكن مختلفة
      if (parsed.data?.token?.access_token) {
        const token = String(parsed.data.token.access_token);
        return token;
      }
      if (parsed.data?.access_token) return parsed.data.access_token;
      if (parsed.data?.token) return parsed.data.token;
      if (parsed.token) return parsed.token;
      if (parsed.result?.token) return parsed.result.token;
      if (parsed.user?.token) return parsed.user.token;
      return null;
    } catch {
      return null;
    }
  }
  return null;
};

const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://hefnawy.brmjatech.co.uk/api';

// Fetch sub categories
export const fetchSubCategories = createAsyncThunk(
  'subCategories/fetchSubCategories',
  async (categoryId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No auth token found');
      }


      const response = await axios.get(`${BASE_URL}/category_subcats/${categoryId}`, {
          headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar'
        }
      });


      if (response.data.status === 'success') {
        return {
          data: response.data.result || [],
          message: response.data.message || 'Sub categories fetched successfully'
        };
      } else {
        throw new Error(response.data.message || 'Failed to fetch sub categories');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Error fetching sub categories');
    }
  }
);

// Add sub category
export const addSubCategory = createAsyncThunk(
  'subCategories/addSubCategory',
  async (formData, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No auth token found');
      }


      const response = await axios.post(`${BASE_URL}/add_new_subcategory`, formData, {
          headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar',
          'Content-Type': 'multipart/form-data'
        }
      });


      if (response.data.status === 'success') {
        return {
          data: response.data.result,
          message: response.data.message || 'Sub category created successfully'
        };
      } else {
        throw new Error(response.data.message || 'Failed to create sub category');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Error creating sub category');
    }
  }
);

// Update sub category
export const updateSubCategory = createAsyncThunk(
  'subCategories/updateSubCategory',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No auth token found');
      }


      const response = await axios.post(`${BASE_URL}/update_new_subcategory/${id}`, data, {
          headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar',
          'Content-Type': 'multipart/form-data'
        }
      });


      if (response.data.status === 'success') {
        return {
          id: id,
          data: response.data.result,
          message: response.data.message || 'Sub category updated successfully'
        };
      } else {
        throw new Error(response.data.message || 'Failed to update sub category');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Error updating sub category');
    }
  }
);

// Update sub category status
export const updateSubCategoryStatus = createAsyncThunk(
  'subCategories/updateSubCategoryStatus',
  async (id, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No auth token found');
      }


      const response = await axios.get(`${BASE_URL}/update_subcategory_status/${id}`, {}, {
          headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar'
        }
      });


      if (response.data.status === 'success') {
        return {
          id: id,
          message: response.data.message || 'Sub category status updated successfully'
        };
      } else {
        throw new Error(response.data.message || 'Failed to update sub category status');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Error updating sub category status');
    }
  }
);

// Delete sub category
export const deleteSubCategory = createAsyncThunk(
  'subCategories/deleteSubCategory',
  async (id, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No auth token found');
      }


      const response = await axios.delete(`${BASE_URL}/delete_subcategory/${id}`, {
          headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar'
        }
      });


      if (response.data.status === 'success') {
        return {
          id: id,
          message: response.data.message || 'Sub category deleted successfully'
        };
      } else {
        throw new Error(response.data.message || 'Failed to delete sub category');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Error deleting sub category');
    }
  }
);

const initialState = {
    data: [],
    loading: false,
    error: null,
  success: false
};

const subCategorySlice = createSlice({
  name: 'subCategories',
  initialState,
  reducers: {
    clearSubCategoryState: (state) => {
      state.data = [];
      state.loading = false;
      state.error = null;
      state.success = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch sub categories
      .addCase(fetchSubCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.success = true;
      })
      .addCase(fetchSubCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Add sub category
      .addCase(addSubCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addSubCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(addSubCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Update sub category
      .addCase(updateSubCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSubCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateSubCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Update sub category status
      .addCase(updateSubCategoryStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSubCategoryStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateSubCategoryStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Delete sub category
      .addCase(deleteSubCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSubCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(deleteSubCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  }
});

export const { clearSubCategoryState } = subCategorySlice.actions;
export default subCategorySlice.reducer;
